import React from "react";

import { WebPageURL } from "../../../models/nominals";
import { ICompareTile, ITileID } from "../../../models/product-compare";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { notEmpty } from "../../../utils/functional";
import { useRequestPath } from "../../../utils/urls";
import { CompareAllModal as CompareAllModalComponent } from "../CompareAllModal";
import { defaults } from "../defaults";
import {
    collapseTray,
    expandTray,
    hideModal,
    removeFromSelected,
} from "../reducers";
import { SelectedProductTray as SelectedProductTrayComponent } from "./SelectedProductTrayContent";

import styles from "./index.module.scss";

const getSelectedTiles = (
    selectedTileIDs: readonly ITileID[],
    tiles: readonly ICompareTile[],
): ICompareTile[] => {
    // Find within map is used so as to preserve the selection order of the tiles.
    return selectedTileIDs
        .map((tileID) => {
            return tiles.find((t) => {
                return t.product_compare_tile.id === tileID;
            });
        })
        .filter(notEmpty);
};

const buildCompareAllURI = (
    tiles: readonly ICompareTile[],
    productComparePageURL: WebPageURL | null,
    requestPath: string,
): string => {
    const tileIDs = tiles.map((tile) => {
        return tile.product_compare_tile.id;
    });
    const baseURL = productComparePageURL;
    const ids = encodeURIComponent(tileIDs.join(","));
    const returnURL = encodeURIComponent(requestPath);
    return `${baseURL}?tiles=${ids}&return_url=${returnURL}`;
};

export const SelectedProductTray = (props: {
    productComparePageURL: WebPageURL | null;
    tiles: readonly ICompareTile[];
    ctaText?: string;
}): React.ReactNode => {
    const dispatch = useAppDispatch();
    const requestPath = useRequestPath();

    const state = useAppSelector((rootState) => {
        return rootState.productcompare || defaults;
    });

    const ctaText = props.ctaText ? props.ctaText : "Compare All Mattresses";

    const onRemoveButtonClick = (tileID: ITileID) => {
        dispatch(removeFromSelected({ tileID }));
    };

    const onCollapseToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (state.trayIsExpanded) {
            dispatch(collapseTray());
        } else {
            dispatch(expandTray());
        }
    };

    const selectedTiles = getSelectedTiles(state.selectedTileIDs, props.tiles);
    const compareAllURI = buildCompareAllURI(
        props.tiles,
        props.productComparePageURL,
        requestPath,
    );

    return (
        <section className={`selected-product-tray ${styles.root}`}>
            <SelectedProductTrayComponent
                selectedTiles={selectedTiles}
                compareAllURI={compareAllURI}
                priceSelectValue={state.priceSelectValue}
                productComparePageURL={props.productComparePageURL}
                onRemoveButtonClick={onRemoveButtonClick}
                onCollapseToggle={onCollapseToggle}
                isExpanded={state.trayIsExpanded}
                ariaLabel={state.trayLabel}
                numProductsAvailable={props.tiles.length}
                ctaText={ctaText}
            />
            <CompareAllModalComponent
                selectedTiles={selectedTiles}
                isModalVisible={state.showCompareModal}
                compareAllURI={compareAllURI}
                productComparePageURL={props.productComparePageURL}
                onCloseModalClick={() => dispatch(hideModal())}
            />
        </section>
    );
};
